import { ApolloLink } from '@apollo/client/core';

import { DuplicateRequestReporter } from '../contracts/duplicate-request-reporter.interface';
import { DuplicateRequestDetectorService } from '../services/duplicate-request-detector.service';
import { currentRoute } from '../utils/current-route';
import { extractPayloadShape } from '../utils/extract-payload-shape';
import { stableStringify } from '../utils/stable-stringify';

interface DuplicateRequestApolloLinkOptions {
	readonly detector: DuplicateRequestDetectorService;
	readonly reporter: DuplicateRequestReporter;
	readonly getRoute: () => string;
}

interface DuplicateRequestApolloContext {
	skipDuplicateRequestMonitoring?: boolean;
}

function getOperationName(operationName: string | undefined): string {
	return operationName && operationName !== '' ? operationName : 'anonymous';
}

export function createDuplicateRequestApolloLink(options: DuplicateRequestApolloLinkOptions): ApolloLink {
	return new ApolloLink((operation, forward) => {
		const context = operation.getContext() as DuplicateRequestApolloContext;

		if (context.skipDuplicateRequestMonitoring) {
			return forward(operation);
		}

		try {
			const operationName = getOperationName(operation.operationName);
			const variables = operation.variables;
			const match = options.detector.track({
				source: 'graphql',
				fingerprint: `${operationName}:${stableStringify(variables)}`,
				groupingKey: operationName,
				operationName,
				route: options.getRoute() || currentRoute(null),
				timestamp: performance.now(),
				payloadShape: extractPayloadShape(variables),
				sameOperationKey: operationName,
			});

			if (match) {
				options.reporter.report(match);
			}
		} catch (error) {
			// Monitoring must never break the underlying request. Swallow and log instead.
			// eslint-disable-next-line no-console
			console.error('[DuplicateRequestMonitoring] apollo link failed', error);
		}

		return forward(operation);
	});
}
